import {FileModel} from '../../../craft/files/file.model'
import {
  GeoPoint,
  ProAssociation,
  ProDirectorySearchItem,
  ProSpokenLanguage,
  SearchProDirectorySorting,
} from '../../../../__generated__/types'
import {Region} from '../../../core/content/types/craftsmen-region-types'
import {IconDefinition} from '@fortawesome/fontawesome-svg-core'
import {HashMap} from '@ngneat/transloco'
import {Type} from '@angular/core'

export const DEFAULT_DISTANCE = 30000
export const DEFAULT_ORDER = SearchProDirectorySorting.RelevanceDesc

export type ProDirectory = {
  pros: DirectoryPro[]
  total: number
}

export interface DirectoryPro {
  id: string
  name: string
  slug: string
  description?: string
  industries: string[]
  logo?: FileModel
  coverImage?: FileModel
  locations: ProPublicLocation[]
  language: string
  isProfilePublished: {
    de: boolean
    fr: boolean
    it: boolean
    en: boolean
  }
  recommendingAgency?: {
    agencyName?: string
  }
  numApprentices: number
  responseTimeInHours?: number
  associations: string[]
  isErneuerbarheizenNetwork: boolean
  additionallySpokenLanguages: ProSpokenLanguage[]
  yearsAsTopPartner?: number[]
  galleryImages: {url: string}[]
}

export interface GooglePlace {
  rating?: number
  userRatingCount?: number
}

export interface ProPublicLocation {
  postalCode: number
  locality: string
  default?: boolean
  geoPoint?: {
    lat: number
    lng: number
  }
  googlePlace?: GooglePlace
}

export function proDirectoryGqlToProDirectory(pro: ProDirectorySearchItem): DirectoryPro {
  const allLocation = pro.locations.map(location => ({
    locality: location.locality,
    postalCode: location.postalCode,
    default: location.default,
    googlePlace: location.googlePlace,
    geoPoint: location.geoPoint,
  }))

  // filter out duplicate locations
  const locations = Array.from(
    allLocation.reduce(
      (acc: {postalCodes: number[]; locations: ProPublicLocation[]}, location) => {
        if (!acc.postalCodes.includes(location.postalCode)) {
          return {postalCodes: [...acc.postalCodes, location.postalCode], locations: [...acc.locations, location]}
        }

        return acc
      },
      {postalCodes: [], locations: []},
    ).locations,
  )

  return {
    id: pro.id,
    name: pro.companyName,
    slug: pro.slug,
    description: pro.description,
    industries: pro.industries.map(i => i.label),
    locations,
    coverImage: pro.coverImage && pro.coverFileKey ? toFileModel(pro.coverFileKey, pro.coverImage) : undefined,
    logo: pro.logoFileKey && pro.logoImage ? toFileModel(pro.logoFileKey, pro.logoImage) : undefined,
    isProfilePublished: pro.isProfilePublished,
    language: pro.language,
    recommendingAgency: {agencyName: pro.recommendingAgency?.name},
    numApprentices: pro.numApprentices,
    responseTimeInHours: pro.responseTimeInHours,
    // filter out "OTHER", it has no value to be shown to the user in the directory
    associations: pro.associations.filter(a => a !== ProAssociation.Other),
    isErneuerbarheizenNetwork: pro.isErneuerbarheizenNetwork,
    additionallySpokenLanguages: pro.additionallySpokenLanguages,
    yearsAsTopPartner: pro.yearsAsTopPartner,
    galleryImages: pro.galleryImages,
  }
}

function toFileModel(fileKey: string, image: {url: string; contentType: string}): FileModel {
  return {
    fileKey,
    uploaded: true,
    url: image.url,
    contentType: image.contentType,
  }
}

export type FilterCriteria = {
  industryIds?: string[]
  offeringIds?: string[]
  searchText?: string
  geoLocation?: GeoPoint
  location?: {
    postalDistrict: string
    zip: number
    distance?: 15000 | 30000 | 60000 | 90000 // in meters from location
  }
  region?: Region | undefined
}

type MetricIcon = IconDefinition | {component: Type<unknown>; inputs: Record<string, unknown>}

export type Metric = {
  label: {localizedValue: string; params?: HashMap}
  icon?: MetricIcon
  info?: {localizedValue: string; params?: HashMap}
  suffix?: {value: string; icon?: MetricIcon}
}
